import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { FitText } from '@/components/FitText';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { money } from '@/lib/helpers';
import { openDashboard } from '@/lib/dashboard';
import { QuickAddMenu } from '@/components/QuickAddMenu';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ScanLine, PackagePlus, ArrowLeftRight, Undo2, Search, ShoppingBag,
  TrendingUp, Receipt, Package, Truck, CreditCard, Gauge, ExternalLink } from 'lucide-react';

const Action = ({ icon: Icon, label, onClick, testid, primary }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex flex-col items-center justify-center gap-2 rounded-2xl border p-3 active:scale-[0.98] transition-transform ${primary ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>
    <Icon className="h-6 w-6" />
    <span className="text-xs font-medium text-center leading-tight">{label}</span>
  </button>
);

const StatCard = ({ label, value, icon: Icon, sub, testid, className = 'p-4' }) => (
  <div data-testid={testid} className={`rounded-2xl border border-border bg-card ${className}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <FitText className="mt-2" align="left"><span className="font-mono text-2xl font-semibold leading-tight tabular-nums">{value}</span></FitText>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function Home() {
  const { user, currentStore, stores, settings, can } = useApp();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get('/reports/dashboard')).data.data,
    refetchInterval: 20000,
  });
  const isOwner = user?.role === 'OWNER';
  const storeStock = data?.store_stock?.find((s) => s.store_id === currentStore);
  const dash = settings?.dashboard || { layout: 'grid2', tiles: [] };
  const gridClass = dash.layout === 'grid1' ? 'grid-cols-1' : dash.layout === 'large' ? 'grid-cols-1' : 'grid-cols-2';
  const bigClass = dash.layout === 'large' ? 'p-6' : 'p-4';

  const enabled = (key) => !dash.tiles?.length || dash.tiles.find((t) => t.key === key)?.enabled;
  const orderedStatKeys = (dash.tiles?.length ? dash.tiles.filter((t) => t.enabled).map((t) => t.key)
    : ['today_sales', 'today_bills', 'stock_value', 'pending_transfers', 'returns_today', 'outstanding_credit'])
    .filter((k) => ['today_sales', 'today_bills', 'stock_value', 'pending_transfers', 'returns_today', 'outstanding_credit'].includes(k))
    .filter((k) => isOwner || !['outstanding_credit', 'returns_today'].includes(k))
    .filter((k) => k !== 'stock_value' || isOwner || (can && can('view_cost')));

  const REG = {
    today_sales: { label: "Today's Sales", value: money(data?.total_sales), icon: TrendingUp, sub: isOwner ? 'All stores' : stores.find((s) => s.id === currentStore)?.name },
    today_bills: { label: "Today's Bills", value: data?.bills_count ?? '—', icon: Receipt },
    stock_value: { label: isOwner ? 'Total Stock Value' : 'Stock Value', value: money(isOwner ? data?.total_stock_value : storeStock?.stock_value), icon: Package, sub: isOwner ? 'All stores' : `${storeStock?.available ?? 0} items` },
    pending_transfers: { label: 'Pending Transfers', value: data?.pending_transfers ?? 0, icon: Truck },
    returns_today: { label: 'Returns Today', value: data?.returns_today ?? 0, icon: Undo2 },
    outstanding_credit: { label: 'Outstanding Credit', value: money(data?.outstanding_credit), icon: CreditCard },
  };

  return (
    <Page>
      <TopBar title="SHABZ CLOSET" />
      <div className="space-y-5 px-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Hi {user?.name || user?.username},</p>
            <h2 className="font-display text-xl font-semibold">Quick actions</h2>
          </div>
          {(isOwner || (can && can('dashboard'))) && <QuickAddMenu />}
        </div>
        {(isOwner || (can && can('dashboard'))) && (
          <button data-testid="home-management-dashboard" onClick={() => navigate('/management')}
            className="flex w-full items-center gap-3 rounded-2xl bg-primary px-4 py-3.5 text-left font-semibold text-primary-foreground transition-transform active:scale-[0.99]">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20"><Gauge className="h-5 w-5" /></span>
            <span className="flex-1 text-sm">Management Dashboard</span>
            <ExternalLink className="h-4 w-4 opacity-80" />
          </button>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Action testid="quick-new-bill" icon={ScanLine} label="New Bill" primary onClick={() => navigate('/billing')} />
          <Action testid="quick-add-stock" icon={PackagePlus} label="Add Stock" onClick={() => navigate('/add-stock')} />
          <Action testid="quick-purchase" icon={ShoppingBag} label="+ Purchase" onClick={() => navigate('/purchase')} />
          <Action testid="quick-transfer" icon={ArrowLeftRight} label="Transfer" onClick={() => navigate('/transfers')} />
          <Action testid="quick-return" icon={Undo2} label="Return / Exchange" onClick={() => navigate('/returns')} />
          <Action testid="quick-find" icon={Search} label="Find Garment" onClick={() => navigate('/stock')} />
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Overview</h2>
          <div className={`grid ${gridClass} gap-3`} data-testid="dashboard-tiles">
            {orderedStatKeys.map((k) => {
              const testid = `stat-${k === 'stock_value' ? 'store-stock' : k === 'today_sales' ? 'today-sales' : k === 'today_bills' ? 'today-bills' : k === 'pending_transfers' ? 'pending-transfers' : k === 'returns_today' ? 'returns' : 'credit'}`;
              const card = <StatCard testid={testid} label={REG[k].label} value={REG[k].value} icon={REG[k].icon} sub={REG[k].sub} className={bigClass} />;
              if (k === 'today_sales') {
                return (
                  <button key={k} type="button" onClick={() => navigate('/sales?period=today')} className="block w-full text-left transition-transform active:scale-[0.99]" data-testid="stat-today-sales-link">
                    {card}
                  </button>
                );
              }
              return <React.Fragment key={k}>{card}</React.Fragment>;
            })}
          </div>
        </div>

        {isOwner && enabled('store_sales') && (
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Per-store today</h2>
            <div className={`grid ${gridClass} gap-3`}>
              {data?.store_stock?.map((s) => (
                <StatCard key={s.store_id} testid={`stat-store-${s.store_id}`} label={`${s.store_name} Sales`}
                  value={money(s.sales_today)} icon={TrendingUp} sub={`${s.available} items · ${money(s.stock_value)} stock`} />
              ))}
            </div>
          </div>
        )}

        {isOwner && enabled('sales_graph') && <StoreSalesGraph stores={stores} />}

        {isOwner && (
          <button data-testid="home-stock-report-link" onClick={() => navigate('/stock-report')}
            className="flex w-full items-center justify-between rounded-2xl border border-border bg-card p-4 text-left active:scale-[0.99] transition-transform">
            <span className="text-sm font-medium">View category &amp; party-wise stock report</span>
            <span className="text-primary">→</span>
          </button>
        )}
      </div>
    </Page>
  );
}

const COLORS = ['hsl(var(--primary))', 'hsl(43 60% 50%)', 'hsl(205 75% 45%)', 'hsl(345 65% 50%)'];
function StoreSalesGraph({ stores }) {
  const [period, setPeriod] = useState('day');
  const { data } = useQuery({
    queryKey: ['store-trend', period],
    queryFn: async () => (await api.get('/reports/store-sales-trend', { params: { period, points: period === 'day' ? 7 : period === 'week' ? 6 : 6 } })).data.data,
  });
  const series = (data?.series || []).map((row) => {
    const o = { label: row.label };
    (data?.stores || []).forEach((s) => { o[s.name] = row[s.id] || 0; });
    return o;
  });
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="dashboard-sales-graph">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold">Store sales comparison</p>
        <div className="flex gap-1">
          {['day', 'week', 'month'].map((p) => (
            <button key={p} data-testid={`graph-period-${p}`} onClick={() => setPeriod(p)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${period === p ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{p}</button>
          ))}
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ left: -12, right: 8, top: 6 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip formatter={(v) => money(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {(data?.stores || []).map((s, i) => (
              <Line key={s.id} type="monotone" dataKey={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
